import { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"

type UserProfile = {
  _id: string
  userId: string
  userName: string
  email: string
  teamId: string
  teamName: string
  hasSelectedTeam: boolean
  enabled?: boolean
  approvalStatus?: "pending" | "approved" | "rejected"
  role?: "ADMIN" | "C_LEVEL" | "MANAGER" | "USER"
  createdAt: string
}

type Team = {
  _id: string
  teamId: string
  teamName: string
  enabled: boolean
}

export function UserManagement() {
  const [users, setUsers] = useState<UserProfile[]>([])
  const [pendingUsers, setPendingUsers] = useState<UserProfile[]>([])
  const [disabledUsers, setDisabledUsers] = useState<UserProfile[]>([])
  const [teams, setTeams] = useState<Team[]>([])
  const [loading, setLoading] = useState(false)
  const [editingUserId, setEditingUserId] = useState<string | null>(null)
  const [selectedTeamId, setSelectedTeamId] = useState("")
  const [editingUserNameId, setEditingUserNameId] = useState<string | null>(null)
  const [newUserName, setNewUserName] = useState("")
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null)
  const [selectedRole, setSelectedRole] = useState<"ADMIN" | "C_LEVEL" | "MANAGER" | "USER">("USER")
  const [activeTab, setActiveTab] = useState<"approved" | "pending" | "disabled">("approved")

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [usersResult, teamsResult] = await Promise.all([
        lumi.entities.userProfiles3.list({ 
          limit: 1000,
          sort: { createdAt: -1 }
        }),
        lumi.entities.teams.list({
          filter: { enabled: true },
          sort: { teamName: 1 }
        })
      ])
      
      const allUsers = usersResult.list as UserProfile[]
      
      // 승인된 사용자와 대기 중인 사용자 분리
      const approved = allUsers.filter(u => u.approvalStatus === "approved" || !u.approvalStatus)
      const pending = allUsers.filter(u => u.approvalStatus === "pending")
      // 비활성화된 사용자는 기본 목록에서 제외하고 별도 탭으로 분리 (데이터는 삭제하지 않음)
      const activeApproved = approved.filter(u => u.enabled !== false)
      const disabled = approved.filter(u => u.enabled === false)

      setUsers(activeApproved)
      setPendingUsers(pending)
      setDisabledUsers(disabled)
      // CEO 팀 제외한 팀 목록만 저장
      const filteredTeams = (teamsResult.list as Team[]).filter(t => t.teamName !== "CEO")
      setTeams(filteredTeams)
    } catch (error) {
      console.error("Failed to load data:", error)
      alert("❌ 데이터 로딩에 실패했습니다.")
    } finally {
      setLoading(false)
    }
  }

  const handleApproveUser = async (user: UserProfile) => {
    const confirmed = window.confirm(
      `${user.userName || user.email}님의 회원가입을 승인하시겠습니까?\n\n` +
      `승인 후 사용자는 로그인하여 시스템을 사용할 수 있습니다.`
    )

    if (!confirmed) return

    try {
      await lumi.entities.userProfiles3.update(user._id, {
        approvalStatus: "approved",
        updatedAt: new Date().toISOString()
      })
      alert(`✅ ${user.userName || user.email}님이 승인되었습니다.`)
      loadData()
    } catch (error) {
      console.error("Failed to approve user:", error)
      alert("❌ 사용자 승인에 실패했습니다.")
    }
  }

  const handleRejectUser = async (user: UserProfile) => {
    const confirmed = window.confirm(
      `${user.userName || user.email}님의 회원가입을 거부하시겠습니까?\n\n` +
      `⚠️ 거부 시 해당 사용자는 로그인할 수 없게 됩니다.`
    )

    if (!confirmed) return

    try {
      await lumi.entities.userProfiles3.update(user._id, {
        approvalStatus: "rejected",
        updatedAt: new Date().toISOString()
      })
      alert(`✅ ${user.userName || user.email}님의 가입이 거부되었습니다.`)
      loadData()
    } catch (error) {
      console.error("Failed to reject user:", error)
      alert("❌ 사용자 거부에 실패했습니다.")
    }
  }

  const handleEditTeam = (user: UserProfile) => {
    setEditingUserId(user._id)
    setSelectedTeamId(user.teamId)
  }

  const handleEditUserName = (user: UserProfile) => {
    setEditingUserNameId(user._id)
    setNewUserName(user.userName)
  }

  const handleEditRole = (user: UserProfile) => {
    setEditingRoleId(user._id)
    setSelectedRole(user.role || "USER")
  }

  const handleSaveRole = async (userId: string) => {
    const user = users.find(u => u._id === userId)
    if (!user) return

    const roleNames = {
      ADMIN: "최고관리자",
      C_LEVEL: "임원",
      MANAGER: "팀장",
      USER: "일반사용자"
    }

    const confirmed = window.confirm(
      `${user.userName}님의 역할을 "${roleNames[user.role || "USER"]}"에서 "${roleNames[selectedRole]}"로 변경하시겠습니까?\n\n` +
      `⚠️ 역할 변경 시 다음과 같이 적용됩니다:\n` +
      `- 최고관리자(ADMIN): 모든 관리 기능 접근 가능\n` +
      `- 임원(C_LEVEL): 관리자 대시보드만 접근 가능\n` +
      `- 팀장(MANAGER): User 기능 + 팀 대시보드 + 주간보고 승인\n` +
      `- 일반사용자(USER): 시간 입력 및 개인 대시보드만 사용 가능`
    )

    if (!confirmed) {
      setEditingRoleId(null)
      return
    }

    try {
      await lumi.entities.userProfiles3.update(userId, {
        role: selectedRole,
        updatedAt: new Date().toISOString()
      })
      alert(`✅ ${user.userName}님의 역할이 "${roleNames[selectedRole]}"로 변경되었습니다.`)
      setEditingRoleId(null)
      loadData()
    } catch (error) {
      console.error("Failed to update role:", error)
      alert("❌ 역할 변경에 실패했습니다.")
    }
  }

  const handleSaveUserName = async (userId: string) => {
    if (!newUserName.trim()) {
      alert("⚠️ 이름을 입력해주세요.")
      return
    }

    const user = users.find(u => u._id === userId)
    if (!user) return

    const confirmed = window.confirm(
      `${user.userName}님의 이름을 "${newUserName}"으로 변경하시겠습니까?\n\n` +
      `⚠️ 주의사항:\n` +
      `- userProfiles3의 이름이 업데이트됩니다.\n` +
      `- 해당 사용자가 입력한 모든 투입시간 데이터(time_entries2)의 userName도 함께 변경됩니다.\n` +
      `- 이 작업은 되돌릴 수 없습니다.`
    )

    if (!confirmed) {
      setEditingUserNameId(null)
      return
    }

    try {
      // 1. userProfiles3 업데이트
      await lumi.entities.userProfiles3.update(userId, {
        userName: newUserName.trim(),
        updatedAt: new Date().toISOString()
      })

      // 2. 해당 userId의 모든 time_entries2 데이터 조회
      const timeEntriesResult = await lumi.entities.time_entries2.list({
        filter: { userId: user.userId },
        limit: 10000
      })

      // 3. 모든 time_entries2 레코드의 userName 업데이트
      const updatePromises = timeEntriesResult.list.map((entry: any) => 
        lumi.entities.time_entries2.update(entry._id, {
          userName: newUserName.trim(),
          updatedAt: new Date().toISOString()
        })
      )

      await Promise.all(updatePromises)

      alert(
        `✅ 이름이 변경되었습니다.\n\n` +
        `- userProfiles3: ${user.userName} → ${newUserName}\n` +
        `- 업데이트된 투입시간 데이터: ${timeEntriesResult.list.length}개`
      )
      
      setEditingUserNameId(null)
      loadData()
    } catch (error) {
      console.error("Failed to update userName:", error)
      alert("❌ 이름 변경에 실패했습니다.")
    }
  }

  const handleSaveTeam = async (userId: string) => {
    if (!selectedTeamId) {
      alert("⚠️ 팀을 선택해주세요.")
      return
    }

    const selectedTeam = teams.find(t => t.teamId === selectedTeamId)
    if (!selectedTeam) return

    const user = users.find(u => u._id === userId)
    if (!user) return

    const confirmed = window.confirm(
      `${user.userName}님의 팀을 "${user.teamName}"에서 "${selectedTeam.teamName}"으로 변경하시겠습니까?\n\n` +
      `⚠️ 변경 이후부터 입력되는 투입시간 데이터에만 새로운 팀 정보가 적용됩니다.\n` +
      `(기존 입력된 데이터는 변경되지 않습니다)`
    )

    if (!confirmed) {
      setEditingUserId(null)
      return
    }

    try {
      await lumi.entities.userProfiles3.update(userId, {
        teamId: selectedTeam.teamId,
        teamName: selectedTeam.teamName,
        updatedAt: new Date().toISOString()
      })
      alert("✅ 팀이 변경되었습니다.\n변경 이후부터 입력되는 데이터에 새로운 팀 정보가 적용됩니다.")
      setEditingUserId(null)
      loadData()
    } catch (error) {
      console.error("Failed to update team:", error)
      alert("❌ 팀 변경에 실패했습니다.")
    }
  }

  const handleDisableUser = async (user: UserProfile) => {
    const action = user.enabled === false ? "활성화" : "비활성화"
    const confirmed = window.confirm(
      `${user.userName}님을 ${action}하시겠습니까?\n\n` +
      `⚠️ ${action} 시 다음과 같이 처리됩니다:\n` +
      (user.enabled === false 
        ? "- 사용자가 다시 로그인하고 시간을 입력할 수 있습니다.\n- 대시보드 통계에 포함됩니다."
        : "- 사용자가 로그인할 수 있으나 대시보드 통계에서 제외됩니다.\n- 기존 입력된 데이터는 삭제되지 않습니다.")
    )

    if (!confirmed) return

    try {
      await lumi.entities.userProfiles3.update(user._id, {
        enabled: user.enabled === false ? true : false,
        updatedAt: new Date().toISOString()
      })
      alert(`✅ ${user.userName}님이 ${action}되었습니다.`)
      loadData()
    } catch (error) {
      console.error("Failed to update user status:", error)
      alert(`❌ 사용자 ${action}에 실패했습니다.`)
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("ko-KR")
  }

  const getTeamColor = (teamId: string) => {
    const colors = [
      { bg: "bg-blue-100", text: "text-blue-800" },
      { bg: "bg-green-100", text: "text-green-800" },
      { bg: "bg-purple-100", text: "text-purple-800" },
      { bg: "bg-pink-100", text: "text-pink-800" },
      { bg: "bg-indigo-100", text: "text-indigo-800" },
      { bg: "bg-yellow-100", text: "text-yellow-800" },
      { bg: "bg-red-100", text: "text-red-800" },
      { bg: "bg-cyan-100", text: "text-cyan-800" },
      { bg: "bg-teal-100", text: "text-teal-800" },
      { bg: "bg-orange-100", text: "text-orange-800" }
    ]
    
    let hash = 0
    for (let i = 0; i < teamId.length; i++) {
      hash = teamId.charCodeAt(i) + ((hash << 5) - hash)
    }
    const index = Math.abs(hash) % colors.length
    return colors[index]
  }

  const getRoleBadge = (role?: "ADMIN" | "C_LEVEL" | "MANAGER" | "USER") => {
    switch (role) {
      case "ADMIN":
        return { bg: "bg-indigo-100", text: "text-indigo-800", label: "관리자" }
      case "C_LEVEL":
        return { bg: "bg-purple-100", text: "text-purple-800", label: "임원" }
      case "MANAGER":
        return { bg: "bg-emerald-100", text: "text-emerald-800", label: "팀장" }
      case "USER":
      default:
        return { bg: "bg-blue-100", text: "text-blue-800", label: "사용자" }
    }
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">사용자 관리</h2>
        <p className="text-sm sm:text-base text-gray-600">직원들의 가입 승인 및 팀 소속을 관리할 수 있습니다</p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-4 sm:mb-6 flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab("approved")}
          className={`px-4 py-2 text-sm sm:text-base font-medium transition-colors ${
            activeTab === "approved"
              ? "text-orange-600 border-b-2 border-orange-600"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          승인된 사용자 ({users.length})
        </button>
        <button
          onClick={() => setActiveTab("pending")}
          className={`px-4 py-2 text-sm sm:text-base font-medium transition-colors relative ${
            activeTab === "pending"
              ? "text-orange-600 border-b-2 border-orange-600"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          승인 대기 ({pendingUsers.length})
          {pendingUsers.length > 0 && (
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
          )}
        </button>
        <button
          onClick={() => setActiveTab("disabled")}
          className={`px-4 py-2 text-sm sm:text-base font-medium transition-colors ${
            activeTab === "disabled"
              ? "text-orange-600 border-b-2 border-orange-600"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          비활성 사용자 ({disabledUsers.length})
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                {activeTab === "approved" ? "활성 사용자" : activeTab === "pending" ? "승인 대기 중" : "비활성 사용자"}
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">
                {activeTab === "approved"
                  ? `활성 상태 사용자 ${users.length}명 (비활성 사용자는 별도 탭에서 확인)`
                  : activeTab === "pending"
                    ? `승인 대기 중인 사용자 ${pendingUsers.length}명`
                    : `비활성화된 사용자 ${disabledUsers.length}명 (데이터는 삭제되지 않았습니다)`}
              </p>
            </div>
            <button
              onClick={loadData}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white text-sm sm:text-base rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? "로딩 중..." : "🔄 새로고침"}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-8 sm:p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-4 border-orange-600 border-t-transparent"></div>
              <p className="mt-4 text-sm sm:text-base text-gray-600">데이터를 불러오는 중...</p>
            </div>
          ) : activeTab === "pending" ? (
            // 승인 대기 탭
            pendingUsers.length === 0 ? (
              <div className="p-8 sm:p-12 text-center">
                <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <p className="text-gray-600 text-sm sm:text-lg">승인 대기 중인 사용자가 없습니다</p>
              </div>
            ) : (
              <table className="w-full min-w-[640px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">사용자</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">이메일</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">가입일</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">작업</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pendingUsers.map((user) => (
                    <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-yellow-100 rounded-full flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                            <span className="text-yellow-600 font-medium text-sm sm:text-base">
                              {(user.userName || user.email)?.charAt(0) || "?"}
                            </span>
                          </div>
                          <div className="text-xs sm:text-sm font-medium text-gray-900">{user.userName || "미입력"}</div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        <div className="text-xs sm:text-sm text-gray-900 max-w-[150px] sm:max-w-none truncate">{user.email}</div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 hidden sm:table-cell">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-left text-xs sm:text-sm font-medium">
                        <div className="flex flex-col sm:flex-row gap-1 sm:gap-3 sm:justify-start">
                          <button
                            onClick={() => handleApproveUser(user)}
                            className="text-green-600 hover:text-green-900 transition-colors text-xs sm:text-sm whitespace-nowrap"
                          >
                            ✓ 승인
                          </button>
                          <button
                            onClick={() => handleRejectUser(user)}
                            className="text-red-600 hover:text-red-900 transition-colors text-xs sm:text-sm whitespace-nowrap"
                          >
                            ✕ 거부
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : activeTab === "disabled" ? (
            // 비활성 사용자 탭
            disabledUsers.length === 0 ? (
              <div className="p-8 sm:p-12 text-center">
                <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                <p className="text-gray-600 text-sm sm:text-lg">비활성화된 사용자가 없습니다</p>
              </div>
            ) : (
              <table className="w-full min-w-[640px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">사용자</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">이메일</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">역할</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">소속 팀</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">가입일</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">작업</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {disabledUsers.map((user) => (
                    <tr key={user._id} className="hover:bg-gray-50 transition-colors opacity-80">
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-full flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                            <span className="text-gray-500 font-medium text-sm sm:text-base">
                              {user.userName?.charAt(0) || "?"}
                            </span>
                          </div>
                          <div className="text-xs sm:text-sm font-medium text-gray-700">{user.userName || "미입력"}</div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        <div className="text-xs sm:text-sm text-gray-600 max-w-[150px] sm:max-w-none truncate">{user.email}</div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <span className={`px-2 sm:px-3 py-1 inline-flex text-xs sm:text-sm leading-5 font-semibold rounded-full ${getRoleBadge(user.role).bg} ${getRoleBadge(user.role).text}`}>
                          {getRoleBadge(user.role).label}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <span className={`px-2 sm:px-3 py-1 inline-flex text-xs sm:text-sm leading-5 font-semibold rounded-full ${getTeamColor(user.teamId).bg} ${getTeamColor(user.teamId).text}`}>
                          {user.teamName}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 hidden sm:table-cell">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-left text-xs sm:text-sm font-medium">
                        <button
                          onClick={() => handleDisableUser(user)}
                          className="text-green-600 hover:text-green-900 transition-colors text-xs sm:text-sm whitespace-nowrap"
                        >
                          ✓ 활성화
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : (
            // 승인된 사용자 탭
            users.length === 0 ? (
              <div className="p-8 sm:p-12 text-center">
                <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <p className="text-gray-600 text-sm sm:text-lg">등록된 사용자가 없습니다</p>
              </div>
            ) : (
              <table className="w-full min-w-[640px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">사용자</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">이메일</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">역할</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">소속 팀</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">가입일</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">작업</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-full flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                            <span className="text-orange-600 font-medium text-sm sm:text-base">
                              {user.userName?.charAt(0) || "?"}
                            </span>
                          </div>
                          {editingUserNameId === user._id ? (
                            <input
                              type="text"
                              value={newUserName}
                              onChange={(e) => setNewUserName(e.target.value)}
                              className="px-2 sm:px-3 py-1 text-xs sm:text-sm border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              placeholder="이름 입력"
                            />
                          ) : (
                            <div className="text-xs sm:text-sm font-medium text-gray-900">{user.userName}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        <div className="text-xs sm:text-sm text-gray-900 max-w-[150px] sm:max-w-none truncate">{user.email}</div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        {editingRoleId === user._id ? (
                          <select
                            value={selectedRole}
                            onChange={(e) => setSelectedRole(e.target.value as "ADMIN" | "C_LEVEL" | "MANAGER" | "USER")}
                            className="px-2 sm:px-3 py-1 text-xs sm:text-sm border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          >
                            <option value="USER">일반사용자</option>
                            <option value="MANAGER">팀장</option>
                            <option value="C_LEVEL">임원</option>
                            <option value="ADMIN">최고관리자</option>
                          </select>
                        ) : (
                          <span className={`px-2 sm:px-3 py-1 inline-flex text-xs sm:text-sm leading-5 font-semibold rounded-full ${getRoleBadge(user.role).bg} ${getRoleBadge(user.role).text}`}>
                            {getRoleBadge(user.role).label}
                          </span>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        {editingUserId === user._id ? (
                          <select
                            value={selectedTeamId}
                            onChange={(e) => setSelectedTeamId(e.target.value)}
                            className="px-2 sm:px-3 py-1 text-xs sm:text-sm border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          >
                            {teams.map((team) => (
                              <option key={team._id} value={team.teamId}>
                                {team.teamName}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className={`px-2 sm:px-3 py-1 inline-flex text-xs sm:text-sm leading-5 font-semibold rounded-full ${getTeamColor(user.teamId).bg} ${getTeamColor(user.teamId).text}`}>
                            {user.teamName}
                          </span>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        {user.enabled === false ? (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            비활성
                          </span>
                        ) : (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            활성
                          </span>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 hidden sm:table-cell">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-left text-xs sm:text-sm font-medium">
                        {editingUserId === user._id ? (
                          <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 sm:justify-start">
                            <button
                              onClick={() => handleSaveTeam(user._id)}
                              className="text-green-600 hover:text-green-900 transition-colors text-xs sm:text-sm"
                            >
                              ✓ 저장
                            </button>
                            <button
                              onClick={() => setEditingUserId(null)}
                              className="text-gray-600 hover:text-gray-900 transition-colors text-xs sm:text-sm"
                            >
                              ✕ 취소
                            </button>
                          </div>
                        ) : editingUserNameId === user._id ? (
                          <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 sm:justify-start">
                            <button
                              onClick={() => handleSaveUserName(user._id)}
                              className="text-green-600 hover:text-green-900 transition-colors text-xs sm:text-sm"
                            >
                              ✓ 저장
                            </button>
                            <button
                              onClick={() => setEditingUserNameId(null)}
                              className="text-gray-600 hover:text-gray-900 transition-colors text-xs sm:text-sm"
                            >
                              ✕ 취소
                            </button>
                          </div>
                        ) : editingRoleId === user._id ? (
                          <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 sm:justify-start">
                            <button
                              onClick={() => handleSaveRole(user._id)}
                              className="text-green-600 hover:text-green-900 transition-colors text-xs sm:text-sm"
                            >
                              ✓ 저장
                            </button>
                            <button
                              onClick={() => setEditingRoleId(null)}
                              className="text-gray-600 hover:text-gray-900 transition-colors text-xs sm:text-sm"
                            >
                              ✕ 취소
                            </button>
                          </div>
                        ) : (
                          <div className="flex flex-col sm:flex-row gap-1 sm:gap-3 sm:justify-start">
                            <button
                              onClick={() => handleEditUserName(user)}
                              className="text-blue-600 hover:text-blue-900 transition-colors text-xs sm:text-sm whitespace-nowrap"
                            >
                              ✏️ 이름 변경
                            </button>
                            <button
                              onClick={() => handleEditRole(user)}
                              className="text-purple-600 hover:text-purple-900 transition-colors text-xs sm:text-sm whitespace-nowrap"
                            >
                              👤 역할 변경
                            </button>
                            <button
                              onClick={() => handleEditTeam(user)}
                              className="text-orange-600 hover:text-orange-900 transition-colors text-xs sm:text-sm whitespace-nowrap"
                            >
                              ✏️ 팀 변경
                            </button>
                            <button
                              onClick={() => handleDisableUser(user)}
                              className={`${user.enabled === false ? "text-green-600 hover:text-green-900" : "text-red-600 hover:text-red-900"} transition-colors text-xs sm:text-sm whitespace-nowrap`}
                            >
                              {user.enabled === false ? "✓ 활성화" : "🗑️ 비활성화"}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>
      </div>
    </div>
  )
}